import { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Box,
  Paper,
  IconButton,
  Chip,
  Avatar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { KeyboardArrowRight, FiberManualRecord } from "@mui/icons-material";
import { format, parseISO, addHours, addMinutes } from "date-fns";
import { getHostelData } from "../api/services/FetchNodeService";
import expIcon from "../images/icons/exp_icon.svg";
import fnbIcon from "../images/icons/fnb_icon.svg";
import { Leaf } from "lucide-react";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2.5),
  boxShadow: theme.shadows[8],
  backgroundColor: "white",
}));

const formatDate = (date) => {
  if (!(date instanceof Date)) {
    date = new Date(date); // Convert to Date object if it's not already
  }

  const options = { day: "2-digit", month: "short", year: "numeric" };
  return date.toLocaleDateString("en-GB", options).replace(/ /g, " ");
};

const InfoLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.warning.main,
  fontWeight: 500,
  fontSize: "0.875rem",
  width: 85,
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 500,
  fontSize: "0.875rem",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const ItemDot = styled(FiberManualRecord)(({ theme }) => ({
  color: theme.palette.success.main,
  fontSize: "0.5rem",
  marginTop: theme.spacing(0.75),
}));

const formatTime = (dateStr) => {
  try {
    // Parse the UTC date string
    const date = new Date(dateStr);

    // Convert to IST by adding 5 hours and 30 minutes (IST offset from UTC)
    date.setHours(date.getUTCHours() + 5);
    date.setMinutes(date.getUTCMinutes() + 30);

    // Extract hours, minutes, and period (AM/PM)
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";

    // Convert to 12-hour format
    hours = hours % 12 || 12; // 0 becomes 12 for 12-hour format
    const formattedMinutes = minutes < 10 ? "0" + minutes : minutes; // Pad single-digit minutes with a leading zero

    // Manually format to 'hh:mm AM/PM'
    return `${hours}:${formattedMinutes} ${ampm}`;
  } catch (e) {
    console.error("Error formatting date:", e);
    return dateStr; // Return the original string if parsing fails
  }
};

const id = localStorage.getItem("hostel_id");
const phone = localStorage.getItem("guestPhone");

const orderHistorys = async () => {
  try {
    const res = await getHostelData(
      `order-history-foodMenu-hostel/${id}/${phone}`
    );
    console.log(res);
    console.log(res.data);
    return res;
  } catch (e) {
    console.log(e);
  }
};

const ItemList = ({ items, compact = true }) => {
  console.log(items);
  const displayItems = compact ? items.slice(0, 5) : items;

  return (
    <Box sx={{ p: 0 }}>
      {/* Header Row */}
      <Box display="flex" justifyContent="space-between" mb={0.5}>
        <Typography
          color="warning.main"
          fontWeight={600}
          // fontSize="1.1rem"
          sx={{ flex: 0.75, fontFamily: "DM Sans, sans-serif" }}
        >
          Items
        </Typography>
        <Typography
          color="warning.main"
          fontWeight={600}
          // fontSize="1.1rem"
          sx={{ minWidth: "42px", textAlign: "center" }}
        >
          Qty
        </Typography>
        <Typography
          color="warning.main"
          fontWeight={600}
          // fontSize="1.1rem"
          sx={{ minWidth: "70px", textAlign: "left" }}
        >
          Amount
        </Typography>
      </Box>

      {/* Items List */}
      {displayItems.map((item, index) => {
        const addonTotal =
          item.add_on_checklist?.reduce(
            (sum, addon) => sum + (addon.rate || 0),
            0
          ) || 0;
        const basePrice = (item.price || 0) + addonTotal;
        const itemAmount = (item.quantity || 0) * basePrice;

        return (
          <Box key={index} sx={{ mb: 2 }}>
            {/* Main Item Row */}
            <Box
              display="flex"
              justifyContent="space-between"
              // alignItems="center"
            >
              <Box sx={{ flex: 0.75 }}>
                <Typography
                  // sx={{ color: "#374151" }}
                  fontSize="1rem"
                  fontWeight={450}
                  color="text.secondary"
                  sx={{
                    maxWidth: "130px",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {item.name}
                </Typography>
                {item.add_on_checklist?.length > 0 && (
                  <Typography
                    color="text.secondary"
                    fontSize="12px"
                    sx={{ mt: 0, opacity: 0.7 }}
                  >
                    Add-ons:{" "}
                    {item.add_on_checklist
                      .map((addon) => addon.name)
                      .join(", ")}{" "}
                  </Typography>
                )}
              </Box>
              <Typography
                color="text.secondary"
                fontSize="1rem"
                fontWeight={450}
                sx={{ minWidth: "42px", textAlign: "center" }}
              >
                {item.quantity}
              </Typography>
              <Typography
                color="text.secondary"
                fontSize="1rem"
                fontWeight={450}
                sx={{ minWidth: "70px", textAlign: "left" }}
              >
                ₹{itemAmount.toFixed(0)}
              </Typography>
            </Box>
          </Box>
        );
      })}

      {/* Show more items indicator if compact */}
      {compact && items.length > 5 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Typography
            color="text.secondary"
            fontSize="0.875rem"
            sx={{ fontStyle: "italic" }}
          >
            +{items.length - 5} more items
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const OrderDetailsDialog = ({ open, onClose, order, guest, phone }) => {
  // console.log(order);

  // inside the component
  const icon =
    order.sub_category_id === "Experience & Merchandise" ? (
      <Avatar src={expIcon} variant="rounded" />
    ) : order.sub_category_id === "Food & Beverages" ? (
      <Avatar src={fnbIcon} variant="rounded" />
    ) : null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" align="center" fontWeight={600} mb={-1}>
          Order Details
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box py={0}>
          <Box display="flex" mb={1}>
            <InfoLabel sx={{ minWidth: 150 }}>Guest Name:</InfoLabel>
            <InfoValue color="text.secondary" sx={{ fontWeight: "450" }}>
              {guest}
            </InfoValue>
          </Box>
          <Box display="flex" mb={1}>
            <InfoLabel sx={{ minWidth: 150 }}>Phone No.:</InfoLabel>
            <InfoValue color="text.secondary" sx={{ fontWeight: "450" }}>
              {phone}
            </InfoValue>
          </Box>
          {order.description && (
            <Box display="flex" mb={1}>
              <InfoLabel sx={{ whiteSpace: "nowrap", minWidth: 150 }}>
                Serving Location:
              </InfoLabel>
              <InfoValue color="text.secondary" sx={{ fontWeight: "450" }}>
                {order.description}
              </InfoValue>
            </Box>
          )}
          <Box display="flex" mb={1}>
            <InfoLabel sx={{ minWidth: 150 }}> Order Time:</InfoLabel>
            <Box textAlign="right">
              <InfoValue component="span">{order.entryBy}</InfoValue>
              {/* <Typography
                component="span"
                color="text.disabled"
                ml={1}
                sx={{ color: "black" }}
                fontWeight={500}
              > */}
              <InfoValue color="text.secondary" sx={{ fontWeight: "450" }}>
                {formatTime(order.createdAt)}
              </InfoValue>
              {/* </Typography> */}
            </Box>
          </Box>
          <Box display="flex" mb={1}>
            <InfoLabel sx={{ minWidth: 150 }}>Order Date:</InfoLabel>
            <InfoValue color="text.secondary" sx={{ fontWeight: "450" }}>
              {formatDate(order.createdAt)}
            </InfoValue>
          </Box>
          <Box mb={2}>
            <Box
              display="flex"
              // justifyContent="space-between"
              alignItems="center"
              mb={0}
            >
              <InfoLabel sx={{ whiteSpace: "nowrap", minWidth: 150 }}>
                Total Amount
              </InfoLabel>
              <InfoValue
                sx={{
                  color: "#3441D8",
                  fontWeight: "bold",
                }}
              >
                ₹{order.total_amount}
              </InfoValue>
            </Box>
            <Typography
              color="text.secondary"
              fontSize="12px"
              sx={{ opacity: 0.7, textAlign: "left" }}
            >
              (excluding GST & discounts)
            </Typography>
          </Box>
          {
            order.special_instructions && (
              <Box display="flex" mb={1}>
                <InfoLabel sx={{ minWidth: 150 }}>Special Instructions:</InfoLabel>
                <InfoValue color="text.secondary" sx={{ fontWeight: "450" }}>
                  {order.special_instructions}
                </InfoValue>
              </Box>
            )
          }
          <Divider sx={{ my: 2 }} />
          <ItemList items={order.billed_item} compact={false} />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const OrderCard = ({ order, guest, phone }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const currDate = new Date();
  currDate.setHours(0, 0, 0, 0);

  const orderDate = new Date(order.date);
  const isInactive = orderDate < currDate || orderDate > currDate;

  // inside the component
  const icon =
    order.sub_category_id === "Experience & Merchandise" ? (
      <Avatar src={expIcon} variant="rounded" />
    ) : order.sub_category_id === "Food & Beverages" ? (
      <Avatar src={fnbIcon} variant="rounded" />
    ) : null;

  return (
    <>
      <StyledCard>
        <CardContent sx={{ marginBottom: -1.2 }}>
          <Box display={"flex"} justifyContent={"center"}>
            {icon}
          </Box>
          <Box display="flex" alignItems="center" mb={0}>
            <Box flex sx={{ textAlign: "left", ml: 0, p: 1, width: "100%" }}>
              <Box
                display="flex"
                // alignItems="center"
                mb={1}
                // justifyContent={"space-between"}
              >
                <InfoLabel sx={{ minWidth: 180, flexShrink: 0 }}>
                  Guest Name:
                </InfoLabel>

                <InfoValue
                  sx={{
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: 1.4,
                    minWidth: 100,
                  }}
                >
                  {guest}
                </InfoValue>
              </Box>
              {order.description && (
                <Box
                  display="flex"
                  // alignItems="center"
                  mb={1}
                  // justifyContent={"space-between"}
                >
                  <InfoLabel sx={{ minWidth: 180, flexShrink: 0 }}>
                    Location:
                  </InfoLabel>
                  <InfoValue
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: 1.4,
                      minWidth: 100,
                    }}
                  >
                    {order.description}
                  </InfoValue>
                </Box>
              )}
              <Box
                display="flex"
                // alignItems="center"
                // justifyContent={"space-between"}
                mb={1}
              >
                <InfoLabel sx={{ minWidth: 180 }}>Order Time:</InfoLabel>
                <InfoValue>{formatTime(order.createdAt)}</InfoValue>
              </Box>
              <Box
                display="flex"
                // alignItems="center"
                mb={1}
                // justifyContent={"space-between"}
              >
                <InfoLabel sx={{ minWidth: 180 }}>Order Date:</InfoLabel>
                <InfoValue>{formatDate(order.createdAt)}</InfoValue>
              </Box>

              <Box mb={2}>
                <Box
                  display="flex"
                  // justifyContent="space-between"
                  // alignItems="center"
                  mb={0}
                >
                  <InfoLabel sx={{ whiteSpace: "nowrap", minWidth: 180 }}>
                    Total Amount
                  </InfoLabel>
                  <InfoValue
                    sx={{
                      color: "#3441D8",
                      fontWeight: "bold",
                      // fontSize: "1.1rem",
                    }}
                  >
                    ₹{order.total_amount}
                  </InfoValue>
                </Box>
                <Typography
                  color="text.secondary"
                  fontSize="12px"
                  sx={{ opacity: 0.7, textAlign: "left" }}
                >
                  (excluding GST & discounts)
                </Typography>
              </Box>
              {order.special_instructions && (
                <Box
                  display="flex"
                  // alignItems="center"
                  mb={1}
                  // justifyContent={"space-between"}
                >
                  <InfoLabel sx={{ minWidth: 180 }}>
                    Special Instructions
                  </InfoLabel>
                  <InfoValue>{order.special_instructions}</InfoValue>
                </Box>
              )}
              <Divider sx={{ my: 3 }} />
              <ItemList items={order.billed_item} />
            </Box>
          </Box>

          <Button
            fullWidth
            onClick={() => setDetailsOpen(true)}
            sx={{
              color: "success.main",
              // mt: -3,
              // margin:3,
              "&:hover": { bgcolor: "success.light" },
            }}
            endIcon={<KeyboardArrowRight />}
          >
            View More
          </Button>
        </CardContent>
      </StyledCard>
      <OrderDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        order={order}
        guest={guest}
        phone={phone}
      />
    </>
  );
};

export default OrderCard;
